import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const TOKEN_STORAGE_KEY = "trade1.token";
const USER_STORAGE_KEY = "trade1.user";

export interface AuthUser {
  id: string;
  email: string;
  is_admin: boolean;
  display_name: string | null;
  /** Effective values (admins bypass their own flags server-side). Used only to
   *  hide nav items and guard routes — enforcement lives in the API. */
  can_backtest?: boolean;
  can_backfill?: boolean;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  clearSession: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_STORAGE_KEY),
  );
  const [user, setUser] = useState<AuthUser | null>(() => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  });

  useEffect(() => {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, [user]);

  // Re-read the user on mount. The stored blob was previously trusted forever,
  // so an admin who was demoted (or a user whose backtest/backfill access was
  // revoked) kept the corresponding nav until they happened to log out. A 401
  // here is handled by the global unauthorized handler.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const { apiFetch } = await import("./api");
        const fresh = await apiFetch<AuthUser>("/users/me");
        if (!cancelled) setUser(fresh);
      } catch {
        // Offline or transient — keep the cached user; the API is the real gate.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deliberately mount-only: re-running on every token change would refetch
    // immediately after login, which LoginPage already does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isAuthenticated: !!token,
      setSession: (t, u) => {
        setToken(t);
        setUser(u);
      },
      clearSession: () => {
        setToken(null);
        setUser(null);
      },
    }),
    [token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}
